import { useEffect, useState } from 'react';
import { Brand } from '../components/Brand';
import { useAuth } from '../lib/auth';
import { ErrorNote } from '../components/ui';
import { api } from '../lib/api';
import { go } from '../lib/router';

/**
 * What `/api/health` says about a demonstration deployment.
 *
 * Absent on a real one, and the whole block below then never renders. The
 * password is null unless an operator set DEMO_PASSWORD, which is them
 * publishing a credential knowingly; without it the addresses are listed and
 * the password still has to be typed.
 */
interface DemoInfo {
  uploadsArePersistent: boolean;
  password: string | null;
  accounts: { name: string; email: string; role: string; shows: string }[];
}

export function SignIn() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState<DemoInfo | null>(null);

  useEffect(() => {
    // Unauthenticated and cheap. A failure here means the API is unreachable,
    // which the sign-in attempt itself will report far better than a banner.
    api.get<{ demo: DemoInfo | null }>('/api/health')
      .then((health) => setDemo(health.demo))
      .catch(() => setDemo(null));
  }, []);

  const submit = async (withEmail: string, withPassword: string) => {
    setBusy(true);
    setError(null);
    try {
      await signIn(withEmail, withPassword);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin-wrap">
      <form
        className="panel signin-panel"
        onSubmit={(e) => {
          e.preventDefault();
          void submit(email, password);
        }}
      >
        <Brand />
        <h1 className="page-title" style={{ marginTop: 16 }}>Sign in</h1>
        <p className="page-description">
          Contract intelligence and patient communications for primary care.
        </p>

        <div className="field">
          <label className="field-label" htmlFor="email">Email address</label>
          <input
            id="email" type="email" autoComplete="username" required
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="password">Password</label>
          <input
            id="password" type="password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <ErrorNote error={error} />

        <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="signin-back">
          <a href="/" onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            go('/');
          }}>← Back to the InBrief website</a>
        </p>

        {demo && <DemoAccounts demo={demo} busy={busy} onChoose={(account) => {
          setEmail(account);
          if (demo.password) {
            setPassword(demo.password);
            void submit(account, demo.password);
          }
        }} />}
      </form>
    </div>
  );
}

/**
 * The demonstration accounts, offered rather than hidden in the repository.
 *
 * There is one per role because that is the point of them: signing in as a
 * receptionist rather than an administrator is how you see what the role
 * matrix actually does. Choosing one fills the form, and signs in directly
 * when the deployment has published a password.
 */
function DemoAccounts({ demo, busy, onChoose }: {
  demo: DemoInfo;
  busy: boolean;
  onChoose: (email: string) => void;
}) {
  return (
    <div className="demo-block">
      <p className="demo-lede">
        <strong>Demonstration deployment.</strong> The practices, contracts and
        funding opportunities in it are invented. No patient data is held here
        and none should be entered.
        {!demo.uploadsArePersistent && ' Anything uploaded is discarded shortly afterwards.'}
      </p>
      {demo.accounts.length === 0 ? null : (
      <>
      <p className="demo-hint">
        {demo.password
          ? 'Choose an account to sign in. Each role sees a different amount.'
          : 'Sign in as any of these. Each role sees a different amount.'}
      </p>
      <ul className="demo-accounts">
        {demo.accounts.map((account) => (
          <li key={account.email}>
            <button
              type="button"
              className="demo-account"
              disabled={busy}
              onClick={() => onChoose(account.email)}
            >
              <span className="demo-account-name">{account.name}</span>
              <span className="demo-account-role">{account.role}</span>
              <span className="demo-account-shows">{account.shows}</span>
            </button>
          </li>
        ))}
      </ul>
      </>
      )}
    </div>
  );
}
